import { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Input } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { Modal } from "ui/Modal"
import { Text, Title } from "ui/Typography"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"

// Decline flow for a pending booking request. Captures a required reason that the
// backend forwards to the client. `isSubmitting` is owned by the caller (it runs the thunk).
const DeclineBookingModal = ({ open, onCancel, onConfirm, clientName, tripDate, isSubmitting = false }) => {
  const { t } = useTranslation()
  const [reason, setReason] = useState("")
  const [reasonError, setReasonError] = useState(false)

  const handleConfirm = () => {
    const trimmed = reason.trim()
    if (!trimmed) {
      setReasonError(true)
      return
    }
    onConfirm(trimmed)
  }

  const handleChange = (event) => {
    setReason(event.target.value)
    if (reasonError) setReasonError(false)
  }

  return (
    <Modal centered width={430} open={open} onCancel={onCancel}>
      <Flex vertical gap={24}>
        <Flex vertical gap={16}>
          <div className={styles.iconWrap}>
            <WarningOutlinedWithBg />
          </div>
          <Title level={3}>Decline Booking</Title>
          <Text>
            Are you sure you want to decline booking for <strong>{clientName}</strong> on{" "}
            <strong>{tripDate}?</strong>
          </Text>
        </Flex>

        <Flex vertical gap={8}>
          <Text className={styles.label}>
            Reason<span className={styles.required}>*</span>
          </Text>
          <Input.TextArea
            value={reason}
            onChange={handleChange}
            placeholder="Enter the reason"
            autoSize={{ minRows: 3, maxRows: 5 }}
            status={reasonError ? "error" : ""}
          />
          {reasonError && <Text className={styles.errorText}>Please enter a reason</Text>}
          <Flex align="center" gap={8} className={styles.infoNote}>
            <InfoCircleOutlined className={styles.infoIcon} />
            <Text className={styles.infoText}>The reason will be displayed to the client</Text>
          </Flex>
        </Flex>

        <Flex gap={12}>
          <Button block variant="outlined" color="primary" onClick={onCancel} disabled={isSubmitting}>
            {t("global.cancel")}
          </Button>
          <Button block type="primary" danger onClick={handleConfirm} loading={isSubmitting}>
            Decline
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

DeclineBookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  clientName: PropTypes.string,
  tripDate: PropTypes.string,
  isSubmitting: PropTypes.bool
}

export default DeclineBookingModal
